// Client-side downscale for onboarding property photos.
//
// Nothing server-side resizes and the upload endpoint caps at 10 MB, so a full-res phone
// photo (12 MP+) can be rejected and six of them is ~60 MB over mobile data. We downscale
// the long edge to ~2048px and re-encode JPEG at 0.8.
//
// If decode/encode fails — most often a HEIC the browser can't decode to a canvas — we
// fall back to the ORIGINAL file rather than blocking the customer. The caller must then
// honour `tooLarge`: when the fallback original still exceeds the cap, tell the customer to
// try a different photo instead of letting the upload fail silently at the server.

export const MAX_UPLOAD_BYTES = 10 * 1024 * 1024; // must match the server cap
const MAX_EDGE = 2048;
const JPEG_QUALITY = 0.8;

export interface ResizeResult {
  file: File;        // the resized file, or the original on fallback
  resized: boolean;  // false = we could not resize and are sending the original
  tooLarge: boolean; // true = `file` still exceeds MAX_UPLOAD_BYTES — caller must warn
}

export async function resizeImage(file: File): Promise<ResizeResult> {
  try {
    const bitmap = await createImageBitmap(file);
    const scale = Math.min(1, MAX_EDGE / Math.max(bitmap.width, bitmap.height));
    const w = Math.max(1, Math.round(bitmap.width * scale));
    const h = Math.max(1, Math.round(bitmap.height * scale));

    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('no 2d context');
    ctx.drawImage(bitmap, 0, 0, w, h);
    bitmap.close?.();

    const blob: Blob = await new Promise((resolve, reject) =>
      canvas.toBlob(
        (b) => (b ? resolve(b) : reject(new Error('toBlob returned null'))),
        'image/jpeg',
        JPEG_QUALITY,
      ),
    );
    const name = file.name.replace(/\.[^.]+$/, '') + '.jpg';
    const out = new File([blob], name, { type: 'image/jpeg' });
    return { file: out, resized: true, tooLarge: out.size > MAX_UPLOAD_BYTES };
  } catch {
    // Could not decode/encode (commonly HEIC). Send the original; the caller warns if it
    // is over the cap so the customer knows to pick a different photo.
    return { file, resized: false, tooLarge: file.size > MAX_UPLOAD_BYTES };
  }
}
